/**
 * Renews an existing key (`665:20906`): the key's own name carries over
 * unchanged, so the only inputs are the new secret and how long the old one
 * stays valid. `POST /ops/keys/{k}/renew` takes the new secret and the
 * chosen `grace_seconds` for the superseded one (api `RenewKeyIn`).
 *
 * The grace control defaults to "immediately" (0 seconds): renewing already
 * means the operator wants the old secret gone, so the default is the choice
 * that leaves it valid for the shortest possible window, and a longer grace
 * period takes a deliberate, explicit pick.
 *
 * The secret field is cleared synchronously, before the request settles
 * either way, for the same reason `NewKeyDialog` clears it.
 */

import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { Sparkles } from 'lucide-react';

import { ApiFailureError, type ApiFailure } from '../../lib/http/errors';
import { useRenewKey } from '../api/queries';
import { FormDialog, fieldControlProps, fieldErrorOf } from '../../chrome/form';
import { Field, FieldError, FieldLabel, FieldTitle } from '../../ui/field';
import { Input } from '../../ui/input';
import { Separator } from '../../ui/separator';
import { Chip } from '../../ui/chip';
import { GracePeriodField, graceSecondsFor, type GraceChoice } from './grace-period';
import { displayKeyFailure, kt } from './messages';

export function RenewKeyDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keyId: string;
  name: string;
  lastFour: string;
  /** The key's `used_by` operations (`api`'s `KeyOut.used_by`, never
   *  re-derived from the operation catalogue), each with its resolved
   *  display name -- kept paired so two operations that share a display
   *  name (the same operation on two networks) still get distinct React
   *  keys below. */
  usedBy: { operationKey: string; name: string }[];
}): ReactNode {
  const [secret, setSecret] = useState('');
  const [graceChoice, setGraceChoice] = useState<GraceChoice>('immediately');
  const [graceCustomValue, setGraceCustomValue] = useState('');
  // Set only once a submit was blocked on an unresolved custom value --
  // until then, an empty custom field stays quiet rather than greeting the
  // operator with red text before they have typed anything.
  const [graceSubmitAttempted, setGraceSubmitAttempted] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const renewMutation = useRenewKey();
  const secretId = useId();
  const secretErrorId = useId();
  const graceLegendId = useId();

  const graceSeconds = graceSecondsFor(graceChoice, graceCustomValue);
  const graceCustomInvalid =
    graceChoice === 'custom' &&
    graceSeconds === null &&
    (graceCustomValue.trim() !== '' || graceSubmitAttempted);

  function reset(): void {
    setSecret('');
    setGraceChoice('immediately');
    setGraceCustomValue('');
    setGraceSubmitAttempted(false);
    setFailure(null);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void | Promise<unknown> {
    event.preventDefault();

    if (graceSeconds === null) {
      setGraceSubmitAttempted(true);
      return;
    }

    const submittedSecret = secret;
    setSecret('');
    return renewMutation
      .mutateAsync({ keyId: props.keyId, secret: submittedSecret, graceSeconds })
      .catch((error: unknown) => {
        setFailure(
          error instanceof ApiFailureError
            ? displayKeyFailure(error.failure)
            : { kind: 'server', status: 500 },
        );
        throw error;
      })
      .finally(() => renewMutation.reset());
  }

  return (
    <FormDialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) reset();
        props.onOpenChange(open);
      }}
      title={kt('renew.title')}
      description={`${props.name}  ••••${props.lastFour}`}
      submitLabel={kt('renew.submit')}
      pending={renewMutation.isPending}
      failure={failure}
      onSubmit={handleSubmit}
    >
      <Field>
        <FieldTitle>{kt('renew.usedBy.label')}</FieldTitle>
        <div className="flex flex-wrap gap-2">
          {props.usedBy.length === 0 ? (
            <Chip tone="neutral">{kt('usedBy.none')}</Chip>
          ) : (
            props.usedBy.map((usage) => (
              <Chip key={usage.operationKey} tone="neutral" icon={<Sparkles aria-hidden />}>
                {usage.name}
              </Chip>
            ))
          )}
        </div>
      </Field>
      <Separator />
      <Field data-invalid={fieldErrorOf(failure, 'secret') !== null || undefined}>
        <FieldLabel htmlFor={secretId}>{kt('renew.secret.label')}</FieldLabel>
        <Input
          id={secretId}
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={secret}
          onChange={(event) => setSecret(event.target.value)}
          required
          {...fieldControlProps(failure, 'secret', secretErrorId)}
        />
        <FieldError id={secretErrorId}>{fieldErrorOf(failure, 'secret')}</FieldError>
      </Field>
      <Separator />
      <GracePeriodField
        idPrefix={graceLegendId}
        legendId={graceLegendId}
        legend={kt('renew.grace.legend')}
        choice={graceChoice}
        onChoiceChange={(choice) => {
          setGraceChoice(choice);
          setGraceSubmitAttempted(false);
        }}
        customValue={graceCustomValue}
        onCustomValueChange={(value) => {
          setGraceCustomValue(value);
          setGraceSubmitAttempted(false);
        }}
        customInvalid={graceCustomInvalid}
      />
    </FormDialog>
  );
}
